import { Dispatch, SetStateAction, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import { v7 as uuidv7 } from "uuid";

import { EditCompanyCardPayloadType } from "../../../../../../Pages/Companies/types";
import { inputFieldMarginBottom } from "../../../../../../shared";
import InputField from "../../../../../InputField/InputField";
import { CompanyCardDto, DetailsDataType } from "../../../../types";
import useCompaniesCardsFormInputs from "../../../hooks/useCompaniesCardsFormInputs";

interface EditCardDialogContentProps {
  cardData: CompanyCardDto;
  companyData: DetailsDataType;
  setPayload: Dispatch<SetStateAction<EditCompanyCardPayloadType | null>>;
  setIsReadyToConfirm: Dispatch<SetStateAction<boolean>>;
  cardNameError: string | null;
  setCardNameError: Dispatch<SetStateAction<string | null>>;
}

const EditCardDialogContent = ({
  cardData,
  companyData,
  setPayload,
  setIsReadyToConfirm,
  cardNameError,
  setCardNameError
}: EditCardDialogContentProps) => {
  const { t } = useTranslation();

  const [cardName, setCardName] = useState<string>(cardData.name);
  const [cardId, setCardId] = useState<string | null>(cardData.companyCardId);
  const [cardNameInputError, setCardNameInputError] = useState<string | null>(
    null
  );
  const [cardIdInputError, setCardIdInputError] = useState<string | null>(null);

  const {
    isCardNameValid,
    validateCardNameLength,
    handleCardNameChangeValue,
    isCardIdValid,
    validateCardId,
    handleCardIdChangeValue,
    handleCardNameAxiosError
  } = useCompaniesCardsFormInputs({
    setCardNameInputError,
    setCardName,
    setCardIdInputError,
    setCardId,
    setCardNameError
  });

  const isPayloadReady =
    Boolean(cardName) &&
    Boolean(cardId) &&
    Boolean(isCardIdValid(cardId)) &&
    Boolean(isCardNameValid(cardName));

  useEffect(() => {
    handleCardNameAxiosError(cardNameError);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardNameError]);

  useEffect(() => {
    cardData &&
      cardId &&
      setPayload({
        id: cardData.id,
        name: cardName.trim(),
        companyCardId: cardId.trim()
      });

    setIsReadyToConfirm(isPayloadReady);
  }, [
    cardData,
    cardId,
    cardName,
    isPayloadReady,
    setIsReadyToConfirm,
    setPayload
  ]);

  return (
    <>
      <InputField
        id={uuidv7()}
        size="medium"
        data-testid="edit-card-company-name"
        fullWidth
        readOnly
        labelLeft={t("Dialog##company")}
        placeholder={companyData?.name}
        customStyle={inputFieldMarginBottom}
      />
      <InputField
        id={uuidv7()}
        testId="edit-card-name-input-field"
        value={cardName}
        size="medium"
        fullWidth
        required
        errorText={cardNameInputError}
        onBlur={validateCardNameLength}
        onChange={handleCardNameChangeValue}
        labelLeft={t("Dialog##card name")}
        placeholder={t("Dialog##enter here")}
        customStyle={inputFieldMarginBottom}
      />
      <InputField
        id={uuidv7()}
        testId="edit-card-id-input-field"
        value={cardId}
        size="medium"
        fullWidth
        required
        errorText={isCardIdValid(cardId) ? "" : cardIdInputError}
        onBlur={validateCardId}
        onChange={handleCardIdChangeValue}
        labelLeft={t("Dialog##company card id")}
        placeholder={t("Dialog##enter here")}
        customStyle={inputFieldMarginBottom}
        infoTooltip={t(`Dialog##enter company card id`)}
      />
    </>
  );
};

export default EditCardDialogContent;
